import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { URLS, useLocalStorage, useSuperPageAssignmentsOverview, STORAGE_KEYS } from "@/utils";
import { Badge, Button, Separator } from "@/components/ui";
import { CheckCircle2, Clock, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSuperPageAssignmentSearch } from "@/utils/useSuperPageAssignmentSearch";
import type { GetSuperPageAssignmentsParams } from "@/services/bedrock/SuperPageServices";
import { SuperPageAssignmentsProgressRing } from "./SuperPageAssignmentsProgressRing";
import { useHistory } from "react-router-dom";
import { SuperPageAssignmentsContent } from "./SuperPageAssignmentsContent";
import { useSuperPageAssignmentVideoPageModal } from "./useSuperPageAssignmentVideoPageModal";
import { SuperPageAssignment } from "@/services/bedrock";

const SuperPageAssignmentsListPreview: React.FC = () => {
  const { modal, onAssignmentClick } = useSuperPageAssignmentVideoPageModal();
  const history = useHistory();
  const [selectedStatus, setSelectedStatus] = useState<SuperPageAssignment.Status | undefined>("REQUESTED");
  const [hasVideoPageTasks, setHasVideoPageTasks] = useLocalStorage(STORAGE_KEYS.HAS_VIDEO_PAGE_TASKS, false);

  const listParams = useMemo((): GetSuperPageAssignmentsParams => {
    return {
      status: selectedStatus,
      extraFields: {
        superPage: ["categories", "videoScript", "templateContentPage"],
        thumbnailUrl: true,
        contentPage: true,
      },
      page: 1,
      perPage: 5,
      sort: "relevance",
    };
  }, [selectedStatus]);

  const { superPageAssignments, isFetching: isFetchingSuperPageAssignments } = useSuperPageAssignmentSearch(listParams);

  const { superPageAssignmentsOverview: overview, isFetching: isFetchingOverview } = useSuperPageAssignmentsOverview();

  const completedCount = overview?.totalCompleted ?? 0;
  const requestedCount = overview?.totalRequested ?? 0;
  const availableCount = overview?.totalAvailable ?? 0;
  const actionableTotal = requestedCount + completedCount;

  useEffect(() => {
    if (overview) {
      setHasVideoPageTasks(overview.total > 0);
    }
  }, [overview, setHasVideoPageTasks]);

  const showAvailableBadge = useMemo(() => {
    return isFetchingOverview || availableCount > 0;
  }, [isFetchingOverview, availableCount]);

  const showCompletedBadge = useMemo(() => {
    return isFetchingOverview || completedCount > 0;
  }, [isFetchingOverview, completedCount]);

  const showRequestedBadge = useMemo(() => {
    return isFetchingOverview || requestedCount > 0;
  }, [isFetchingOverview, requestedCount]);

  useEffect(() => {
    if (selectedStatus === "COMPLETED" && !showCompletedBadge) {
      setSelectedStatus(undefined);
    } else if (selectedStatus === "REQUESTED" && !showRequestedBadge) {
      setSelectedStatus(undefined);
    } else if (selectedStatus === "AVAILABLE" && !showAvailableBadge) {
      setSelectedStatus(undefined);
    }
  }, [showCompletedBadge, showRequestedBadge, showAvailableBadge, selectedStatus]);

  const goToSuperPagesList = () => {
    history.push(URLS.player.superPages);
  };

  const showFullListRef = useRef(false);

  if (!hasVideoPageTasks) return null;

  let content: React.ReactNode;
  if (!showFullListRef.current && actionableTotal > 0 && completedCount === actionableTotal) {
    content = (
      <div className="flex items-center gap-2">
        <SuperPageAssignmentsProgressRing
          completedCount={completedCount}
          total={actionableTotal}
          size="xs"
          skeleton={isFetchingOverview}
        />
        <div className="flex flex-col gap-0.5">
          <div className="text-sm font-semibold leading-tight text-foreground">You're all caught up!</div>
          <div className="text-xs leading-snug text-muted-foreground">
            Every assignment is complete. You can
            <Button variant="link" size="inline" onClick={goToSuperPagesList}>
              open your Video Page Assignments list
            </Button>
            anytime to review.
          </div>
        </div>
      </div>
    );
  } else {
    showFullListRef.current = !isFetchingOverview;

    content = (
      <div className="flex flex-row gap-8 items-center">
        <div className="flex flex-col items-center justify-start gap-2">
          <SuperPageAssignmentsProgressRing
            completedCount={completedCount}
            total={actionableTotal}
            size="lg"
            skeleton={isFetchingOverview}
          />
          <div className="text-center text-sm text-muted-foreground" data-skeleton={isFetchingOverview}>
            {completedCount}/{Math.max(actionableTotal, 0)} Assignments
          </div>
        </div>

        <Separator orientation="vertical" />

        <div className="flex flex-col gap-2 flex-grow">
          <div className="flex flex-row gap-4 flex-wrap">
            {showRequestedBadge && (
              <Badge
                className={cn(
                  "cursor-pointer gap-2",
                  selectedStatus === "REQUESTED"
                    ? "bg-amber-600 hover:bg-amber-500"
                    : "border-amber-300 text-amber-800 hover:text-amber-800 hover:bg-amber-50"
                )}
                data-skeleton={isFetchingOverview}
                size="medium"
                variant={selectedStatus === "REQUESTED" ? "default" : "outline"}
                onClick={() => setSelectedStatus(prev => (prev === "REQUESTED" ? undefined : "REQUESTED"))}
              >
                <Clock className="size-4" />
                Requested ({requestedCount})
              </Badge>
            )}
            {showAvailableBadge && (
              <Badge
                className={cn(
                  "cursor-pointer gap-2",
                  selectedStatus === "AVAILABLE"
                    ? "bg-sky-600 hover:bg-sky-500"
                    : "border-sky-300 text-sky-800 hover:text-sky-800 hover:bg-sky-50"
                )}
                data-skeleton={isFetchingOverview}
                size="medium"
                variant={selectedStatus === "AVAILABLE" ? "default" : "outline"}
                onClick={() => setSelectedStatus(prev => (prev === "AVAILABLE" ? undefined : "AVAILABLE"))}
              >
                <Inbox className="size-4" />
                Available ({availableCount})
              </Badge>
            )}
            {showCompletedBadge && (
              <Badge
                className={cn(
                  "cursor-pointer gap-2",
                  selectedStatus === "COMPLETED"
                    ? "bg-emerald-600 hover:bg-emerald-500"
                    : "border-emerald-300 text-emerald-800 hover:text-emerald-800 hover:bg-emerald-50"
                )}
                data-skeleton={isFetchingOverview}
                size="medium"
                variant={selectedStatus === "COMPLETED" ? "default" : "outline"}
                onClick={() => setSelectedStatus(prev => (prev === "COMPLETED" ? undefined : "COMPLETED"))}
              >
                <CheckCircle2 className="size-4" />
                Completed ({completedCount})
              </Badge>
            )}
            <Button
              variant="link"
              onClick={goToSuperPagesList}
              children="View all"
              className="ml-auto"
              data-skeleton={isFetchingOverview}
            />
          </div>

          <div className="flex flex-wrap gap-x-5 gap-y-4 min-h-50">
            <SuperPageAssignmentsContent
              content={superPageAssignments?.content ?? []}
              isFetching={isFetchingSuperPageAssignments}
              onAssignmentClick={onAssignmentClick}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="text-xl font-semibold text-foreground">Video Page Assignments</div>
      {content}
      {modal}
    </div>
  );
};

export { SuperPageAssignmentsListPreview };
